import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Droplets, FlaskConical, Package, Sprout, Thermometer } from "lucide-react";
import { Wordmark } from "@/components/Navbar";
import { Eyebrow, Reveal } from "@/components/Reveal";
import Footer from "@/components/Footer";

const IMG = {
  hero: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?crop=entropy&cs=srgb&fm=jpg&q=85",
  seed: "https://images.unsplash.com/photo-1552592074-ea7a91b851b3?crop=entropy&cs=srgb&fm=jpg&q=85",
  press: "https://images.pexels.com/photos/877220/pexels-photo-877220.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bottle: "https://customer-assets-cm19k8pv.emergentagent.net/job_tsuigva-global/artifacts/a5ea5abc7befec64_1E60C092-4DAB-490F-ACE8-3ADD5C3A8E3F.png",
};

const OILS = [
  { name: "Groundnut", tagline: "Roasted aroma, Vitamin E rich", image: "https://customer-assets-cm19k8pv.emergentagent.net/job_tsuigva-global/artifacts/a5ea5abc7befec64_1E60C092-4DAB-490F-ACE8-3ADD5C3A8E3F.png" },
  { name: "Sunflower", tagline: "Light, mild, everyday gold", image: "https://images.unsplash.com/photo-1552592074-ea7a91b851b3?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Sesame", tagline: "Deep nutty gingelly character", image: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Flaxseed", tagline: "Omega-3 ALA powerhouse", image: "https://images.unsplash.com/photo-1611608927037-4e8da6aa170e?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Extra Virgin Olive", tagline: "First press, low acidity", image: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?crop=entropy&cs=srgb&fm=jpg&q=85" },
];

const JOURNEY = [
  { icon: Sprout, n: "01", title: "Handpicked Seed Selection", text: "Bold peanuts, sunflower, sesame and flax are graded at origin by our field teams. Only dense, oil-rich lots enter the press." },
  { icon: Droplets, n: "02", title: "Sun Drying & De-dusting", text: "Seeds rest under the sun to reach perfect moisture, then pass multi-stage cleaning — no stones, no dust, no shortcuts." },
  { icon: Thermometer, n: "03", title: "Wooden Press Below 45°C", text: "Slow-turning wooden ghanis crush the seed gently. No heat shock, no nutrient loss — just oil the way our grandparents knew it." },
  { icon: FlaskConical, n: "04", title: "Triple Sediment Filtration", text: "The oil settles and passes through natural cloth filters three times. Never refined, bleached or deodorised." },
  { icon: Package, n: "05", title: "Nitrogen-Flush Bottling", text: "Every batch is lab-verified, then nitrogen-flushed and sealed — aroma locked in from our press room to your customer's shelf." },
];

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const STORY = [
  {
    eyebrow: "Chapter One",
    title: "The seed comes first",
    text: "Great oil is decided long before the press turns. We buy directly from farmer collectives across Telangana, Andhra Pradesh and Tamil Nadu, grading every lot for density, moisture and oil content. What doesn't meet export grade simply never enters our godown.",
    image: IMG.seed,
    alt: "Sunflower seeds and golden oil",
  },
  {
    eyebrow: "Chapter Two",
    title: "Pressed the slow way",
    text: "Inside our press room, wooden ghanis turn slowly — never faster than tradition allows, never hotter than 45°C. The seed gives up its oil gently, keeping its enzymes, antioxidants and true aroma alive. It takes longer. That's the point.",
    image: IMG.press,
    alt: "Traditional oil pressing",
    flip: true,
  },
  {
    eyebrow: "Chapter Three",
    title: "Sealed for the journey",
    text: "After triple filtration and lab verification, every bottle and tin is nitrogen-flushed before sealing. Whether it travels to a Dubai supermarket or a Hamburg distributor, the first drop poured tastes exactly like the day it left the press.",
    image: IMG.bottle,
    alt: "Cold pressed oil in a glass bottle",
  },
];

export default function OilStory() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <div className="bg-cream">
      <div className="grain-overlay" />

      <header className="fixed inset-x-0 top-0 z-50 border-b border-cream/10 bg-night/85 backdrop-blur-md">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-8 lg:px-16">
          <a href="/" data-testid="oil-story-logo-link" aria-label="Back to home">
            <Wordmark dark />
          </a>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="oil-story-back-home-link" className="hidden items-center gap-2 text-sm font-medium text-cream/70 transition-colors hover:text-amberwarm sm:inline-flex">
              <ArrowLeft size={15} /> Home
            </a>
            <a href="/#quote" data-testid="oil-story-get-quote-button" className="rounded-full bg-chilli px-5 py-2.5 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-[1.04]">
              Get a Quote
            </a>
          </div>
        </nav>
      </header>

      <section ref={heroRef} data-testid="oil-story-hero" className="relative flex min-h-[85vh] items-center overflow-hidden bg-night">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img src={IMG.hero} alt="Cold pressed oil being poured" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-night/95 via-night/80 to-night/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-transparent to-night/50" />
        </motion.div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-24 sm:px-8 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="h-px w-12 bg-amberwarm" />
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-amberwarm sm:text-sm">The Tsugiva Oil Story</span>
          </motion.div>
          <h1 className="max-w-3xl font-serif text-4xl font-bold leading-[1.05] tracking-tight text-cream sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.3}>From Wooden Press,</MaskedLine>
            <MaskedLine delay={0.48} className="italic text-amberwarm">To Your Bottle.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg"
          >
            Five oils. One promise: nothing refined, nothing rushed. This is how a seed from an Indian
            farm becomes a bottle of Tsugiva.
          </motion.p>
        </div>
      </section>

      <section data-testid="oil-story-chapters" className="bg-cream py-20 md:py-28">
        <div className="mx-auto max-w-7xl space-y-24 px-4 sm:px-8 lg:px-16">
          {STORY.map((s, i) => (
            <div key={s.title} className={`grid items-center gap-12 lg:grid-cols-2 lg:gap-20`}>
              <Reveal className={s.flip ? "lg:order-2" : ""}>
                <div className="overflow-hidden rounded-3xl">
                  <img src={s.image} alt={s.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.04]" />
                </div>
              </Reveal>
              <Reveal delay={0.12} className={s.flip ? "lg:order-1" : ""}>
                <Eyebrow>{s.eyebrow}</Eyebrow>
                <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight text-ink sm:text-4xl">{s.title}</h2>
                <p className="mt-5 text-base leading-relaxed text-smoke">{s.text}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="oil-story-journey" className="bg-night py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-4 sm:px-8 lg:px-16">
          <Reveal className="text-center">
            <Eyebrow dark>The Journey</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight text-cream sm:text-4xl lg:text-5xl">
              Five steps. Zero shortcuts.
            </h2>
          </Reveal>
          <div className="relative mt-16">
            <div className="absolute bottom-0 left-[27px] top-0 w-px bg-cream/15 sm:left-1/2" />
            {JOURNEY.map((s, i) => (
              <Reveal key={s.n} delay={0.06 * i} className="relative mb-12 last:mb-0">
                <div className={`flex items-start gap-6 sm:w-1/2 ${i % 2 === 0 ? "sm:pr-14" : "sm:ml-auto sm:flex-row-reverse sm:pl-14 sm:text-right"}`}>
                  <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-amberwarm/40 bg-night font-serif text-lg font-bold text-amberwarm shadow-[0_10px_30px_-10px_rgba(212,139,22,0.5)]">
                    {s.n}
                  </span>
                  <div data-testid={`oil-journey-step-${s.n}`}>
                    <h3 className="font-serif text-xl font-semibold text-cream">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-cream/60">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="oil-story-collection" className="bg-sand py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal className="max-w-2xl">
            <Eyebrow>The Collection</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">
              Five oils, one wooden press
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
            {OILS.map((o, i) => (
              <Reveal key={o.name} delay={i * 0.07}>
                <div data-testid={`oil-collection-${o.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group overflow-hidden rounded-2xl border border-stone2 bg-white transition-all duration-500 hover:-translate-y-2 hover:border-gold hover:shadow-[0_20px_50px_-18px_rgba(212,139,22,0.4)]">
                  <div className="aspect-square overflow-hidden">
                    <img src={o.image} alt={`${o.name} oil`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.08]" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-base font-semibold text-ink">{o.name}</h3>
                    <p className="mt-1 text-xs text-pebble">{o.tagline}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col items-center gap-5 rounded-3xl bg-botanical px-8 py-12 text-center">
              <h3 className="max-w-xl font-serif text-2xl font-semibold text-cream sm:text-3xl">
                Taste the difference slow pressing makes
              </h3>
              <p className="max-w-lg text-sm text-cream/70">
                Bulk flexi-tanks for processors, private-label bottles for retail — tell us your market and we'll quote within one business day.
              </p>
              <a href="/#quote" data-testid="oil-story-cta-button" className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-night transition-transform duration-300 hover:scale-[1.04]">
                Request a Wholesale Quote
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
